import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Calendar, Plus, Edit, Trash2, Clock, AlertCircle, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';
import { talents } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import { TextField, DatePicker, Autocomplete } from '@anyemp/ui-kit';
import { dateTypes, getDateTypeLabel, DEPARTURE_DATE_TYPES } from '../../utils/dateTypeUtils';
import { useAuth } from '../../hooks/useAuth';
import ConfirmDialog from '../UI/ConfirmDialog';
import { fmtTzDateTimeHuman } from '../../utils/dateFormatUtils';

const sortedDateTypes = [...dateTypes].sort((a, b) => a.label.localeCompare(b.label));

const DateForm = ({ formData, setFormData, onSubmit, onCancel, isNew }) => (
  <div className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Autocomplete
        fullWidth
        label="Date Type *"
        value={formData.date_type}
        onChange={(value) => setFormData(p => ({ ...p, date_type: value || '' }))}
        placeholder="Select date type"
        options={sortedDateTypes}
        required
      />
      <DatePicker
        label="Date & Time"
        size="small"
        required
        fullWidth
        showTime
        format="DD.MM.YYYY HH:mm"
        value={formData.date_value ? new Date(formData.date_value) : null}
        onChange={(d) => setFormData(p => ({ ...p, date_value: d ? d.toISOString() : '' }))}
      />
    </div>
    <div className="mt-3">
      <TextField
        label="Description"
        multiline
        rows={2}
        size="small"
        fullWidth
        value={formData.description}
        onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
        placeholder="Enter description or notes..."
      />
    </div>
    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
      <button
        type="button"
        onClick={onSubmit}
        className="min-h-10 w-full px-3 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors sm:w-auto"
      >
        {isNew ? 'Add Date' : 'Save'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-10 w-full px-3 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm text-light-text-secondary dark:text-dark-text-secondary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors sm:w-auto"
      >
        Cancel
      </button>
    </div>
  </div>
);

const ImportantDatesTab = forwardRef(({ talentId, talentType, showAddButton = true }, ref) => {
  const { hasPermission } = useAuth();

  const datesResource = useMemo(() => {
    if (talentType) return `${talentType}_important_dates`;
    return 'important_dates';
  }, [talentType]);

  const canCreate = useMemo(() => hasPermission('talent-service', datesResource, 'create'), [hasPermission, datesResource]);
  const canUpdate = useMemo(() => hasPermission('talent-service', datesResource, 'update'), [hasPermission, datesResource]);
  const canDelete = useMemo(() => hasPermission('talent-service', datesResource, 'delete'), [hasPermission, datesResource]);

  // null = closed | { id: 'new' } = add form at top | { id: X, ... } = edit inline
  const [editingDate, setEditingDate] = useState(null);
  const [formData, setFormData] = useState({ date_type: '', date_value: '', description: '' });
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // date entry to delete

  const handleCreate = () => {
    setEditingDate({ id: 'new' });
    setFormData({ date_type: '', date_value: '', description: '' });
  };

  useImperativeHandle(ref, () => ({ openForm: handleCreate }));

  useEffect(() => { fetchDates(); }, [talentId]);

  const fetchDates = async () => {
    try {
      setLoading(true);
      const response = await talents.getTalentImportantDates(talentId);
      if (response.success) {
        setDates(response.data);
      } else {
        setError('Failed to fetch dates');
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
      setError('Error loading dates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const departureLabel = DEPARTURE_DATE_TYPES[formData.date_type];
    if (departureLabel) {
      const hasStartWork = dates.some(d => d.date_type === 'start_work' && d.id !== editingDate?.id);
      if (!hasStartWork) {
        setError(`Cannot add a "${departureLabel}" date without an existing "Start Work" date for this talent.`);
        return;
      }
    }

    try {
      if (editingDate?.id === 'new') {
        await talents.addTalentImportantDate(talentId, formData);
      } else {
        await talents.updateTalentImportantDate(editingDate.id, formData);
      }
      setEditingDate(null);
      setFormData({ date_type: '', date_value: '', description: '' });
      setError(null);
      fetchDates();
    } catch (err) {
      console.error('Error saving date:', err);
      toast.error('Error saving date');
    }
  };

  const handleEdit = (date) => {
    setEditingDate(date);
    setFormData({
      date_type: date.date_type,
      date_value: date.date_value,
      description: date.description || ''
    });
  };

  const handleDelete = (dateEntry) => {
    setDeleteConfirm(dateEntry);
  };

  const confirmDelete = async () => {
    try {
      await talents.deleteTalentImportantDate(deleteConfirm.id);
      fetchDates();
    } catch (err) {
      console.error('Error deleting date:', err);
      toast.error('Error deleting date');
    }
  };

  const handleCancel = () => {
    setEditingDate(null);
    setFormData({ date_type: '', date_value: '', description: '' });
  };

  const formatDate = (dateString) => fmtTzDateTimeHuman(dateString);

  const getDateStatusIcon = (date) => {
    if (date.is_overdue) return <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
    if (date.is_upcoming) return <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    return <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
  };

  const getDateStatusBadge = (date) => {
    if (date.is_overdue) return (
      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded">Overdue</span>
    );
    if (date.is_upcoming) return (
      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded">Upcoming</span>
    );
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  return (
    <>
    <div className={showAddButton ? 'p-6 bg-light-bg-primary dark:bg-dark-bg-primary' : ''}>
      {showAddButton && (
        <div className="mb-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center space-x-2">
            <CalendarDays className="w-5 h-5" />
            <span>Important Dates</span>
          </h3>
          {canCreate && (
            <button
              onClick={handleCreate}
              className="inline-flex min-h-10 items-center gap-1.5 self-end px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Date</span>
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add form — appears at top */}
      {editingDate?.id === 'new' && (
        <div className="mb-3">
          <DateForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isNew
          />
        </div>
      )}

      {/* Dates list */}
      {dates.length === 0 ? (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-4">No important dates yet</p>
      ) : (
        <div className="space-y-3">
          {dates.map((date) => (
            editingDate?.id === date.id ? (
              /* Edit form replaces card inline */
              <DateForm
                key={date.id}
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isNew={false}
              />
            ) : (
              <div key={date.id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {getDateStatusIcon(date)}
                      <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                        {getDateTypeLabel(date.date_type)}
                      </span>
                      {getDateStatusBadge(date)}
                    </div>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{formatDate(date.date_value)}</p>
                    {date.description && (
                      <p className="mt-1 break-words text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{date.description}</p>
                    )}
                    <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />{formatDate(date.created_at)}
                    </p>
                  </div>
                  {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-1 shrink-0">
                      {canUpdate && (
                        <button
                          onClick={() => handleEdit(date)}
                          className="flex h-10 w-10 items-center justify-center text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(date)}
                          className="flex h-10 w-10 items-center justify-center text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Date"
      message={deleteConfirm
        ? `Are you sure you want to delete "${getDateTypeLabel(deleteConfirm.date_type)}"? This action cannot be undone.`
        : ''}
      confirmText="Delete"
    />
    </>
  );
});

export default ImportantDatesTab;
